import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LI } from "../../../../AbstractElements";
import { languageData } from "../../../../Data/Layout/LanguageData";
import { LanguageDataType } from "../../../../Types/Layout/Header";
import { useAuth } from "../../../../hooks/useAuth";
import { toast } from "react-toastify";

const Language = () => {
  const [open, setOpen] = useState(false);
  const { i18n } = useTranslation();
  const { user, refreshUser } = useAuth();

  // Mapear código de idioma del usuario (ES, EN) a parámetro de i18n (es, en)
  const userLanguageToI18n: { [key: string]: string } = {
    "ES": "es",
    "EN": "en",
  };

  // Mapear parámetro de i18n a código de idioma del usuario
  const i18nToUserLanguage: { [key: string]: string } = {
    "es": "ES",
    "en": "EN",
  };

  // Estado inicial del flag - siempre empieza con "us" y se actualiza en useEffect
  const [flag, setFlag] = useState("us");
  const [isInitialized, setIsInitialized] = useState(false);

  // Sincronizar idioma cuando el usuario cambia o cuando se carga desde localStorage
  useEffect(() => {
    if (isInitialized || !i18n) return; // Si ya se inicializó o i18n no está disponible, salir
    
    try {
      let targetLang = "es";
      let targetFlag = "us";

      // Prioridad 1: Idioma guardado en localStorage (de cambios anteriores sin backend)
      const savedI18nLanguage = localStorage.getItem('selectedI18nLanguage');
      if (savedI18nLanguage) {
        targetLang = savedI18nLanguage;
      } 
      // Prioridad 2: Idioma del usuario (del backend)
      else if (user?.language) {
        targetLang = userLanguageToI18n[user.language] || "es";
      } 
      // Prioridad 3: Idioma actual de i18n
      else if (i18n.language) {
        targetLang = i18n.language;
      }

      const langData = languageData.find(l => l.languageParameter === targetLang);
      if (langData) {
        targetFlag = langData.languageIconClassName;
        setFlag(targetFlag);
        setIsInitialized(true);
        
        // Solo cambiar idioma si es diferente al actual y no se ha inicializado aún
        if (i18n.language !== targetLang) {
          // Usar setTimeout para evitar problemas de inicialización
          setTimeout(() => {
            i18n.changeLanguage(targetLang).catch((error: unknown) => {
              console.error('Error al cambiar idioma:', error);
            });
          }, 100);
        }
      } else {
        setIsInitialized(true);
      }
    } catch (error) {
      console.error('Error en useEffect de Language:', error);
      setIsInitialized(true); // Marcar como inicializado incluso si hay error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language, isInitialized]); // Agregado isInitialized para evitar múltiples ejecuciones

  const changeLanguage = (lng: LanguageDataType) => {
    try {
      // Cambiar idioma en i18n inmediatamente
      setFlag(lng.languageIconClassName);
      i18n.changeLanguage(lng.languageParameter);
      setOpen(false);

      // Guardar el idioma seleccionado en localStorage para persistencia
      const userLanguageCode = i18nToUserLanguage[lng.languageParameter] || "ES";
      localStorage.setItem('selectedLanguage', userLanguageCode);
      localStorage.setItem('selectedI18nLanguage', lng.languageParameter);

      // Si hay usuario logueado, actualizar el idioma solo en memoria (localStorage)
      // No intentamos actualizar en el backend porque el endpoint no está funcionando correctamente
      // El idioma se guardará en localStorage y se usará cuando se recargue la página
      if (user) {
        // Actualizar el usuario en memoria con el nuevo idioma
        const updatedUser = {
          ...user,
          language: userLanguageCode
        };
        
        // Guardar en localStorage para persistencia
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Nota: No actualizamos el contexto directamente porque refreshUser() 
        // sobrescribiría el cambio al obtener el usuario del backend.
        // El idioma se mantendrá en localStorage y se aplicará en el próximo login
        // o cuando se recargue la página (el useEffect leerá el idioma del localStorage)
        
        toast.success(`Idioma cambiado a ${lng.languageName}`);
      } else {
        toast.success(`Idioma cambiado a ${lng.languageName}`);
      }
    } catch (error: any) {
      console.error('Error al cambiar idioma:', error);
      toast.error('Error al cambiar idioma');
      // Revertir cambio si falla
      if (user?.language) {
        const i18nLang = userLanguageToI18n[user.language] || "es";
        const langData = languageData.find(l => l.languageParameter === i18nLang);
        if (langData) {
          setFlag(langData.languageIconClassName);
          i18n.changeLanguage(i18nLang);
        }
      }
    }
  };

  const LanguageSelection = (open: boolean) => {
    if (open) {
      setOpen(!open);
    } else {
      setOpen(!open);
    }
  };

  return (
    <LI className="language-nav">
      <div className={`translate_wrapper ${open ? "active" : ""}`}>
        <div className="current_lang">
          <div className="lang" onClick={() => LanguageSelection(open)}>
            <i className={`flag-icon fi fi-${flag}`}></i>
          </div>
        </div>
        <div className={`more_lang ${open ? "active" : ""}`}>
          {languageData.map((data, index) => (
            <div className="lang selected" key={index} onClick={() => changeLanguage(data)}>
              <i className={`flag-icon fi fi-${data.languageIconClassName}`}></i>
              <span className="lang-txt">
                {data.languageName}
                {data.subTitle && <span> ({data.subTitle})</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    </LI>
  );
};

export default Language;
